import { routes } from "@/lib/routes";
import { SITE_TREE } from "@/lib/site-structure/tree";
import type { CardLink, Crumb, SiteNode } from "@/lib/site-structure/types";

const BY_PATH: ReadonlyMap<string, SiteNode> = new Map(SITE_TREE.map((node) => [node.path, node]));

/** Normalises "/a/b/" and "a/b" to "/a/b"; the home path stays "/". */
function toSitePath(pathOrSlug: string): string {
  const trimmed = pathOrSlug.replace(/^\/+|\/+$/g, "");
  return trimmed ? `/${trimmed}` : routes.home;
}

export function getSiteNode(path: string): SiteNode | undefined {
  return BY_PATH.get(toSitePath(path));
}

function requireNode(path: string): SiteNode {
  const node = getSiteNode(path);
  if (!node) throw new Error(`[site-structure] no node for ${path}`);
  return node;
}

/**
 * The pages between "Startseite" and this page, nearest last: the explicit
 * `trail` if the node has one, else the `parent` chain.
 */
function crumbsAbove(node: SiteNode): Crumb[] {
  if (node.trail) {
    return node.trail.map((step) => ({
      name: step.crumb ?? requireNode(step.path).crumb,
      path: step.path,
    }));
  }
  const chain: Crumb[] = [];
  const seen = new Set<string>([node.path]);
  let parent = node.parent;
  while (parent) {
    if (seen.has(parent)) throw new Error(`[site-structure] parent cycle at ${parent}`);
    seen.add(parent);
    const parentNode = requireNode(parent);
    chain.unshift({ name: parentNode.crumb, path: parentNode.path });
    parent = parentNode.parent;
  }
  return chain;
}

/**
 * The breadcrumb trail for a page: "Startseite", the pages above it, the page
 * itself. One list feeds both the visible breadcrumb and the BreadcrumbList
 * JSON-LD, so the two cannot disagree. Returns `[]` for the home page and for
 * paths the tree does not know.
 */
export function getTrail(path: string, currentName?: string): Crumb[] {
  const node = getSiteNode(path);
  if (!node || node.path === routes.home) return [];
  return [
    { name: "Startseite", path: routes.home },
    ...crumbsAbove(node),
    { name: currentName ?? node.crumb, path: node.path },
  ];
}

/**
 * Every page above this one, nearest first, following each step's own
 * ancestors too. A Wissen article whose trail ends at „Land verpachten“ so
 * reaches „Fläche anbieten“ above it.
 */
function ancestorsOf(node: SiteNode, seen = new Set<string>([node.path])): SiteNode[] {
  const direct = node.trail
    ? [...node.trail].reverse().map((step) => requireNode(step.path))
    : node.parent
      ? [requireNode(node.parent)]
      : [];
  const result: SiteNode[] = [];
  for (const step of direct) {
    if (seen.has(step.path)) continue;
    seen.add(step.path);
    result.push(step, ...ancestorsOf(step, seen));
  }
  return result;
}

/**
 * The level-2 page this page links up to: the node's `upTo`, else the nearest
 * level-2 ancestor. Hubs and level-2 pages themselves link to nothing.
 */
export function getUpLink(path: string): SiteNode | undefined {
  const node = getSiteNode(path);
  if (!node || node.level === 1 || node.level === 2 || node.level === "utility") return undefined;
  if (node.upTo === null) return undefined;
  if (node.upTo) return requireNode(node.upTo);
  return ancestorsOf(node).find((ancestor) => ancestor.level === 2);
}

/**
 * The level-2 pages that belong on a hub, in tree order: those whose
 * breadcrumb parent is the hub, plus those that name it in `hubs`.
 */
export function getHubMembers(hubPath: string): SiteNode[] {
  const hub = toSitePath(hubPath);
  return SITE_TREE.filter(
    (node) => node.level === 2 && (node.parent === hub || node.hubs?.includes(hub))
  );
}

/** The pages a hub's card grid lists, in the hub's `cardOrder` if it has one. */
export function getHubCards(hubPath: string): SiteNode[] {
  const order = getSiteNode(hubPath)?.cardOrder;
  return order ? order.map(requireNode) : getHubMembers(hubPath);
}

/** The level-3 pages directly below a level-2 page, in tree order. */
export function getChildPages(path: string): SiteNode[] {
  const parent = toSitePath(path);
  return SITE_TREE.filter((node) => node.level === 3 && node.parent === parent);
}

/**
 * The links a hub card shows under a page: its curated links, else the
 * page's level-3 children under their crumbs.
 */
export function getCardLinks(node: SiteNode): CardLink[] {
  if (node.card?.links) return [...node.card.links];
  return getChildPages(node.path).map((child) => ({ path: child.path, label: child.crumb }));
}
